import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2, XCircle, ArrowRight, FileText, RotateCw,
  Sparkles, Download, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import GuardianPortfolioPanel from "./qenva/GuardianPortfolioPanel";

/**
 * CrossDocCompare — V2.
 *
 * Renders a single comparison record using the QENVA Design System :
 *   - GuardianPortfolioPanel variant="crosscheck" as the navy header
 *   - Findings are grouped by severity (Critical → Informational), each
 *     in a collapsible block (so 200 findings remain readable)
 *   - Demand vs Provision shown side-by-side in a 2-column grid
 *   - Why / Suggested fix live inline below the comparison row to keep
 *     the layout flat (no nested expansion)
 *   - Document names appear in the panel title (no separate Doc A / B
 *     cards — removes duplication)
 */

const SEVERITY_ORDER = ["Critical", "High", "Medium", "Low", "Informational"];
const SEVERITY_RANK = {
  Critical: 0, High: 1, Medium: 2, Low: 3, Informational: 4,
};
const SEVERITY_TONE = {
  Critical:      { rail: "bg-rose-500",   pill: "bg-rose-50 text-rose-700 border-rose-200",      dot: "bg-rose-500" },
  High:          { rail: "bg-orange-500", pill: "bg-orange-50 text-orange-700 border-orange-200", dot: "bg-orange-500" },
  Medium:        { rail: "bg-amber-500",  pill: "bg-amber-50 text-amber-800 border-amber-200",   dot: "bg-amber-500" },
  Low:           { rail: "bg-emerald-500",pill: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  Informational: { rail: "bg-zinc-400",   pill: "bg-zinc-50 text-zinc-700 border-zinc-200",      dot: "bg-zinc-400" },
};

function fmtValue(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number" && Math.abs(v) >= 1000) {
    if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(v % 1_000_000 ? 1 : 0)}M`;
    return `$${(v / 1_000).toFixed(0)}K`;
  }
  if (typeof v === "object") {
    // Structured operand (e.g. covenant pair) — render a compact JSON snippet
    try {
      const compact = JSON.stringify(v);
      return compact.length > 80 ? compact.slice(0, 77) + "…" : compact;
    } catch (_) {
      return "—";
    }
  }
  return String(v);
}

function isFindingResolved(f) {
  return f.resolved === true || (typeof f.resolved === "object" && f.resolved?.at);
}

function ClaimCell({ claim, kindLabel, missingLabel, testId }) {
  if (!claim) {
    return (
      <div data-testid={testId} className="px-3 py-2 rounded-lg border border-dashed border-zinc-300 bg-zinc-50/50">
        <p className="text-[10.5px] uppercase tracking-wider text-zinc-400 mb-0.5 font-bold">{kindLabel}</p>
        <p className="text-[13px] text-zinc-400 italic">{missingLabel}</p>
      </div>
    );
  }
  return (
    <div data-testid={testId} className="px-3 py-2 rounded-lg border border-zinc-200 bg-white">
      <p className="text-[10.5px] uppercase tracking-wider text-zinc-500 mb-0.5 font-bold">{kindLabel}</p>
      <p className="text-[12.5px] text-zinc-700 truncate" title={claim.label}>{claim.label}</p>
      <p className="text-[14px] font-display font-bold text-qenva-navy tabular-nums mt-0.5">{fmtValue(claim.value)}</p>
    </div>
  );
}

export default function CrossDocCompare({ comparison, docA, docB, onRefresh, onResolved }) {
  const { t } = useTranslation("crosscheck");
  const c = comparison || {};
  const findings = c.findings || [];
  const [resolving, setResolving] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [collapsed, setCollapsed] = useState(new Set()); // group keys collapsed

  // Group findings by severity
  const groupedFindings = useMemo(() => {
    const groups = SEVERITY_ORDER.reduce((acc, s) => { acc[s] = []; return acc; }, {});
    findings.forEach((f, idx) => {
      const sev = SEVERITY_ORDER.includes(f.severity) ? f.severity : "Low";
      groups[sev].push({ ...f, _idx: idx });
    });
    return groups;
  }, [findings]);

  const meta = useMemo(() => {
    let open = 0;
    let resolved = 0;
    let critical = 0;
    for (const f of findings) {
      const r = isFindingResolved(f);
      if (r) resolved += 1; else open += 1;
      if (f.severity === "Critical" && !r) critical += 1;
    }
    return { total: findings.length, open, resolved, critical };
  }, [findings]);

  function toggleGroup(key) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  const handleResolve = async (idx, resolved) => {
    setResolving(idx);
    try {
      const res = await api.patch(
        `/comparisons/${c.id}/findings/${idx}`,
        { resolved, note: null },
      );
      toast.success(resolved ? "Finding marked resolved" : "Finding reopened");
      onResolved?.(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    } finally {
      setResolving(null);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await api.post("/comparisons", {
        type: c.type,
        doc_a_id: c.doc_a_id,
        doc_b_id: c.doc_b_id,
        force_refresh: true,
      });
      toast.success("Comparison re-run");
      onRefresh?.(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Re-run failed");
    } finally {
      setRefreshing(false);
    }
  };

  const docANameShort = docA?.filename || c.doc_a_id?.slice(0, 8) || "Doc A";
  const docBNameShort = docB?.filename || c.doc_b_id?.slice(0, 8) || "Doc B";

  const panelMetrics = [
    { key: "findings", value: meta.total, label: t("detail.metrics.findings"), tone: "white" },
    { key: "open", value: meta.open, label: t("detail.metrics.open"), tone: meta.open > 0 ? "info" : "neutral" },
    { key: "resolved", value: meta.resolved, label: t("detail.metrics.resolved"), tone: "success" },
    { key: "critical", value: meta.critical, label: t("detail.metrics.critical"), tone: meta.critical > 0 ? "danger" : "neutral" },
  ];

  return (
    <div className="space-y-6" data-testid="cross-doc-compare">
      <GuardianPortfolioPanel
        testId="crosscheck-detail-panel"
        variant="crosscheck"
        kicker={t("detail.panel_kicker", { type: c.display_name || c.type || "—" })}
        title={t("detail.panel_title", { docA: docANameShort, docB: docBNameShort })}
        subtitle={t("detail.panel_subtitle")}
        metrics={panelMetrics}
        primaryAction={{
          label: refreshing ? "…" : t("detail.primary_cta"),
          onClick: handleRefresh,
          icon: RotateCw,
        }}
        lastAnalysis={c.created_at ? { iso: c.created_at } : undefined}
      />

      {/* Findings body */}
      {findings.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-2xl p-10 text-center" data-testid="compare-empty-state">
          <CheckCircle2 size={28} className="text-emerald-500 mx-auto mb-3" />
          <p className="font-display font-bold text-qenva-navy text-[15px]">{t("detail.all_resolved_title")}</p>
          <p className="text-[12.5px] text-zinc-500 mt-1 max-w-md mx-auto">{t("detail.all_resolved_subtitle")}</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="findings-grouped">
          {SEVERITY_ORDER.map((sev) => {
            const group = groupedFindings[sev];
            if (!group || group.length === 0) return null;
            const tone = SEVERITY_TONE[sev];
            const isCollapsed = collapsed.has(sev);
            return (
              <section
                key={sev}
                className="rounded-2xl border border-zinc-200 bg-white overflow-hidden"
                data-testid={`findings-group-${sev.toLowerCase()}`}
              >
                <button
                  type="button"
                  onClick={() => toggleGroup(sev)}
                  className="w-full px-5 py-3 flex items-center gap-3 hover:bg-zinc-50 transition-colors border-b border-zinc-200"
                  data-testid={`findings-group-${sev.toLowerCase()}-toggle`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} />
                  <p className="font-display font-bold text-qenva-navy text-[14px] flex-1 text-left">
                    {t(`detail.group.${sev}`)}
                  </p>
                  <span className="text-[11px] text-zinc-500 tabular-nums">{group.length}</span>
                  {isCollapsed ? <ChevronRight size={16} className="text-zinc-400" /> : <ChevronDown size={16} className="text-zinc-400" />}
                </button>
                {!isCollapsed && (
                  <ul className="divide-y divide-zinc-100">
                    {group.map((f) => {
                      const idx = f._idx;
                      const resolved = isFindingResolved(f);
                      return (
                        <li
                          key={idx}
                          className={`flex gap-0 transition-opacity ${resolved ? "opacity-60" : ""}`}
                          data-testid={`finding-${idx}`}
                        >
                          {/* Left severity rail */}
                          <div className={`w-1 shrink-0 ${tone.rail}`} />
                          <div className="flex-1 px-5 py-4 min-w-0">
                            <header className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                              <div className="min-w-0">
                                <h3 className="font-display font-bold text-qenva-navy text-[14px] tracking-tight" data-testid={`finding-${idx}-label`}>
                                  {f.label || "(sans intitulé)"}
                                </h3>
                                {f.note && (
                                  <p className="text-[11px] text-zinc-500 mt-1 italic">{f.note}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span
                                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-bold uppercase border ${tone.pill}`}
                                  data-testid={`finding-${idx}-severity`}
                                >
                                  {sev}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleResolve(idx, !resolved)}
                                  disabled={resolving === idx}
                                  data-testid={`finding-${idx}-resolve-button`}
                                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                                    resolved
                                      ? "bg-zinc-50 text-zinc-600 hover:bg-zinc-100"
                                      : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                                  }`}
                                >
                                  {resolved ? <><XCircle size={12} /> {t("detail.reopen_cta")}</> : <><CheckCircle2 size={12} /> {t("detail.resolve_cta")}</>}
                                </button>
                              </div>
                            </header>

                            <div
                              className="grid sm:grid-cols-[1fr_auto_1fr] gap-3 items-center mb-3"
                              data-testid={`finding-${idx}-comparison`}
                            >
                              <ClaimCell
                                claim={f.demand}
                                kindLabel={t("detail.demand_label")}
                                missingLabel={t("detail.missing_value")}
                                testId={`finding-${idx}-demand`}
                              />
                              <ArrowRight size={14} className="text-zinc-400 mx-auto rotate-90 sm:rotate-0" />
                              <ClaimCell
                                claim={f.provision}
                                kindLabel={t("detail.provision_label")}
                                missingLabel={t("detail.missing_value")}
                                testId={`finding-${idx}-provision`}
                              />
                            </div>

                            {f.why && (
                              <p className="text-[12.5px] text-zinc-700 leading-relaxed mb-2" data-testid={`finding-${idx}-why`}>
                                <span className="font-bold text-qenva-navy">{t("detail.why_label")} : </span>
                                {f.why}
                              </p>
                            )}
                            {f.suggested_fix && (
                              <div className="px-3 py-2.5 rounded-lg bg-qenva-blue/5 border border-qenva-blue/15" data-testid={`finding-${idx}-fix`}>
                                <p className="text-[10.5px] uppercase tracking-wider text-qenva-blue font-bold mb-1">{t("detail.fix_label")}</p>
                                <p className="text-[12.5px] text-zinc-800 leading-relaxed">{f.suggested_fix}</p>
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
